import { useState, useCallback, useRef, useEffect } from "react";

interface UseAutoSaveOptions {
	/** Increment this to re-trigger the debounce timer while isDirty stays true */
	changeToken?: number;
	debounceMs?: number;
	isDirty: boolean;
	onSave: () => Promise<void | boolean>;
}

interface UseAutoSaveResult {
	isSaving: boolean;
	lastSavedAt: Date | null;
	triggerSave: () => void;
}

export const useAutoSave = ({
	isDirty,
	onSave,
	changeToken = 0,
	debounceMs = 2500,
}: UseAutoSaveOptions): UseAutoSaveResult => {
	const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const onSaveRef = useRef(onSave);
	const isMountedRef = useRef(true);

	onSaveRef.current = onSave;

	const clearTimer = useCallback(() => {
		if (timerRef.current !== null) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	const executeSave = useCallback(async () => {
		if (!isMountedRef.current) {
			return;
		}
		setIsSaving(true);
		try {
			const result = await onSaveRef.current();
			// Only mark as saved if onSave didn't return false (skipped)
			if (isMountedRef.current && result !== false) {
				setLastSavedAt(new Date());
			}
		} catch {
			// Save failed — don't update lastSavedAt
		} finally {
			if (isMountedRef.current) {
				setIsSaving(false);
			}
		}
	}, []);

	const triggerSave = useCallback(() => {
		clearTimer();
		timerRef.current = setTimeout(() => {
			executeSave();
		}, debounceMs);
	}, [clearTimer, executeSave, debounceMs]);

	useEffect(() => {
		if (isDirty) {
			triggerSave();
		}
		return clearTimer;
	}, [isDirty, changeToken, triggerSave, clearTimer]);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			clearTimer();
		};
	}, [clearTimer]);

	return {
		lastSavedAt,
		isSaving,
		triggerSave,
	};
};
